import React from "react";
import PropTypes from "prop-types";
import MatchNightRsvpDialog from "../match-night/MatchNightRsvpDialog";
import { formatNightDateLabel } from "../match-night/cetTime";
import useCountdown from "../match-night/useCountdown";
import useMatchNights from "../match-night/useMatchNights";
import useMyMatchNights from "../match-night/useMyMatchNights";

const STORAGE_KEY = "ouro_match_night_promo_dismissed";
const REFRESH_INTERVAL_MS = 10 * 60 * 1000;

const LobbyMatchNightContext = React.createContext(null);

function readDismissedNightId() {
    try {
        return localStorage.getItem(STORAGE_KEY);
    } catch {
        return null;
    }
}

function persistDismissedNightId(nightId) {
    try {
        localStorage.setItem(STORAGE_KEY, nightId);
    } catch {
        // localStorage unavailable
    }
}

function clearDismissedNightId() {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        // localStorage unavailable
    }
}

function isUpcoming(item) {
    const scheduledAt = new Date(item?.night?.scheduledAt);
    return (
        scheduledAt instanceof Date &&
        !Number.isNaN(scheduledAt.getTime()) &&
        scheduledAt > new Date()
    );
}

function getStatusLabel(status) {
    if (status === "waitlisted") return "Waitlisted";
    if (status === "pending_verification") return "Pending verification";
    return "You're in";
}

function getCountsLine(night) {
    if (!night) return "";
    const base =
        night.confirmedCount >= night.minToFire
            ? `${night.confirmedCount} confirmed`
            : `${night.confirmedCount}/${night.minToFire} confirmed`;
    return night.pendingCount > 0
        ? `${base}, ${night.pendingCount} in review`
        : base;
}

export function LobbyMatchNightProvider({ children }) {
    const {
        myRsvps,
        loading: myRsvpsLoading,
        basePath,
        refetch: refetchMyRsvps,
    } = useMyMatchNights();
    const {
        nights,
        loading: nightsLoading,
        refetch: refetchNights,
    } = useMatchNights();
    const [dismissedNightId, setDismissedNightId] =
        React.useState(readDismissedNightId);
    const [rsvpDialogNight, setRsvpDialogNight] = React.useState(null);
    const [cancellingRsvpId, setCancellingRsvpId] = React.useState(null);
    const [cancelError, setCancelError] = React.useState(null);

    const refresh = React.useCallback(() => {
        refetchMyRsvps();
        refetchNights();
    }, [refetchMyRsvps, refetchNights]);

    React.useEffect(() => {
        const interval = setInterval(refresh, REFRESH_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [refresh]);

    const upcomingRsvps = React.useMemo(
        () =>
            myRsvps
                .filter(isUpcoming)
                .sort(
                    (a, b) =>
                        new Date(a.night.scheduledAt) -
                        new Date(b.night.scheduledAt),
                ),
        [myRsvps],
    );
    const nextRsvp = upcomingRsvps[0] || null;
    const nextNight = nextRsvp?.night || nights[0] || null;
    const rsvp = nextRsvp?.rsvp || null;
    const countdown = useCountdown(
        new Date(nextNight?.scheduledAt || null),
    );

    const loading = myRsvpsLoading || nightsLoading;
    const available = !loading && Boolean(nextNight);
    const hidden = available && dismissedNightId === nextNight.id;

    const handleHide = React.useCallback(() => {
        if (!nextNight?.id) return;
        persistDismissedNightId(nextNight.id);
        setDismissedNightId(nextNight.id);
    }, [nextNight?.id]);

    const handleShow = React.useCallback(() => {
        clearDismissedNightId();
        setDismissedNightId(null);
    }, []);

    const handleRsvpSuccess = React.useCallback(() => {
        setRsvpDialogNight(null);
        clearDismissedNightId();
        setDismissedNightId(null);
        refresh();
    }, [refresh]);

    const handleCancel = React.useCallback(async () => {
        if (!nextRsvp?.rsvp?.id) return;
        setCancellingRsvpId(nextRsvp.rsvp.id);
        setCancelError(null);

        try {
            const response = await fetch(
                `${basePath}/api/v1/match-nights/rsvps/${nextRsvp.rsvp.id}`,
                {
                    method: "DELETE",
                    credentials: "include",
                },
            );
            const data = await response.json().catch(() => null);
            if (!response.ok || !data?.ok) {
                throw new Error(
                    data?.message || "Could not cancel RSVP.",
                );
            }
            refresh();
        } catch (error) {
            setCancelError(
                error?.message || "Could not cancel RSVP.",
            );
        } finally {
            setCancellingRsvpId(null);
        }
    }, [basePath, nextRsvp, refresh]);

    const value = React.useMemo(
        () => ({
            available,
            hidden,
            loading,
            night: nextNight,
            rsvp,
            registered: Boolean(nextRsvp),
            dateLabel: nextNight
                ? formatNightDateLabel(
                      new Date(nextNight.scheduledAt),
                      nextNight.region,
                  )
                : "",
            countdownLabel: countdown.label,
            countsLabel: getCountsLine(nextNight),
            statusLabel: getStatusLabel(rsvp?.status),
            cancelError,
            cancelling: cancellingRsvpId === rsvp?.id,
            hide: handleHide,
            show: handleShow,
            reserve: () => setRsvpDialogNight(nextNight),
            cancelRsvp: handleCancel,
        }),
        [
            available,
            hidden,
            loading,
            nextNight,
            rsvp,
            nextRsvp,
            countdown.label,
            cancelError,
            cancellingRsvpId,
            handleHide,
            handleShow,
            handleCancel,
        ],
    );

    return (
        <LobbyMatchNightContext.Provider value={value}>
            {children}
            <MatchNightRsvpDialog
                open={Boolean(rsvpDialogNight)}
                night={rsvpDialogNight}
                basePath={basePath}
                onClose={() => setRsvpDialogNight(null)}
                onRsvpSuccess={handleRsvpSuccess}
            />
        </LobbyMatchNightContext.Provider>
    );
}

export function useLobbyMatchNight() {
    const context = React.useContext(LobbyMatchNightContext);
    if (!context) {
        throw new Error(
            "useLobbyMatchNight must be used inside LobbyMatchNightProvider.",
        );
    }
    return context;
}

LobbyMatchNightProvider.propTypes = {
    children: PropTypes.node.isRequired,
};
